import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Stack, Typography, CircularProgress, Button, Paper } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PageHeader from './../../components/PageHeader/PageHeader';
import SteeringRecoTable from './SteeringRecoTable';
import MultiVotingCard from './MultiVotingCard';
import {
  fetchRecommendationOptions,
  fetchUsers,
  getStepStatus,
  saveVotingData,
  getRegionOptions,
  fetchVotingDataV2,
  fetchUploadsInProgress,
  fetchUploadsInProgressByUserId,
  fetchUniqueL7Customers
} from './servies';
import VotingStepper from './VotingStepper';
import VotingFilter from './VotingFilters';
import { useOkta } from '../../hooks/oktaHook';
import {
  getColumns,
  getData,
  restructureData,
  steeringReco,
  functionalFeedback,
  regionalDecisions,
  finalDecisions
} from './utils';
import {
  setEditedRows,
  setRecommendationOptions,
  setVotingTableData,
  setTagUsers,
  setStepperStatus,
  setShowSave
} from '../../redux/FeedbackCollation/feedbackCollationSlice';
import { triggerNotification } from '../../utils/NotificationContext';
import {
  GENERAL_USER,
  REGIONAL_USER_TYPE,
  CHANNEL_USER_TYPE,
  feedbackCollationColumnOrder,
  CORE_USER,
  TPO_ADMIN
} from '../../utils/Constants';
import { ProgressBar, Tooltip } from 'pepsico-ds';
import { getSanitizedMemberList } from '../SetupTeam/ExcludeMembers';
import { getUrlWithParams } from '../../utils/Utils';

export const LinearProgressWithLabel = (props) => {
  return (
    <Stack direction={'row'} alignItems="center" justifyContent={'space-between'}>
      <Typography variant="body2" sx={{ color: '#616161' }} noWrap>
        Progress on SKU Votes
      </Typography>
      <Box sx={{ width: '40%', mr: 1 }}>
        <ProgressBar variant="determinate" {...props} max={props.totalValue} />
      </Box>
      <Typography variant="body2" sx={{ color: '#616161' }}>
        {props.value}/{props.totalValue}
      </Typography>
    </Stack>
  );
};

const FeedbackCollation = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const intervalIdRef = useRef(null);
  const { search } = useLocation();
  const newParams = new URLSearchParams(search);
  const selectedCycleId = Number(newParams.get('cycleId'));
  const selectedCycle = newParams.get('cycleName');
  const bu = newParams.get('bu');
  const [currentVotingTeam, setCurrentVotingTeam] = useState('');
  const [columns, setColumns] = useState([]);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [totalSKUCount, setTotalSKUCount] = useState(0);
  const [totalVoted, setTotalVoted] = useState(0);
  const [checkedRows, setCheckedRows] = useState([]);
  const [vote, setVote] = useState({});
  const [disableDecisionButton, setDisableDecisionButton] = useState(true);
  const [areVotesApplied, setAreVotesApplied] = useState(true);
  const [areVotesSaved, setAreVotesSaved] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [saveLoader, setSaveLoader] = useState(false);
  const [stepLevelState, setStepLevelState] = useState(1);
  const [showVotingViewScreen, setShowVotingViewScreen] = useState(false);
  // Stepper state variables
  const [userType, setUserType] = useState('');
  const [userLevel, setUserLevel] = useState('');
  const [disableUndo, setDisableUndo] = useState(true);
  const [regionOption, setRegionOption] = useState([]);

  const feedbackCollation = useSelector((state) => state.feedbackCollation);
  const { recommendationOptions, tagUsers, votingTableData, editedRows } = feedbackCollation;
  // Check buttons
  const [showApply, setShowApply] = useState(true);
  const [showUndo, setShowUndo] = useState(disableUndo);
  const [showUploadDownload, setShowUploadDownload] = useState(true);
  const [filterPagination, setFilterPagination] = useState(false); // no of pages to show, initialize to 5
  const [progressVisible, setProgressVisible] = useState(false); // State to manage progressbar visibility
  const perPageRowCount = 3000; //default per page row = 500

  const [uploadStatusMsg, setUploadStatusMsg] = useState({});
  const [fullResData, setFullResData] = useState([]);
  const [filteredLevel2Data, setFilteredLevel2Data] = useState([]);
  const [uniqueL7CustomersList, setUniqueL7CustomersList] = useState([]);
  const [votingData, setVotingData] = useState([]);
  let saveStatus = useSelector((state) => state.feedbackCollation.saveState);

  // Advance filter category
  const productAttr = [
    'tposku',
    'sku name',
    'brand',
    'segment',
    'sub-brand',
    'sub-category',
    'packSize',
    'upc/gtin'
  ];
  const perforamceMetric = [
    'revenue',
    'volume qty',
    'revenue growth',
    'volume growth',
    'profit %',
    'flowback %',
    'waste %',
    'complexity',
    'store count',
    'distribution %',
    'facility %'
  ];
  const operationalInp = ['channel importance', 'inventory', 'complexity'];
  let decisions = ['quadrant'];
  if (currentVotingTeam === 'Steering Reco') {
    decisions = [...['action reco'], ...decisions];
  } else if (
    currentVotingTeam === 'Regional Decisions' ||
    currentVotingTeam === 'Functional Feedback'
  ) {
    decisions = [...['steering team recommendation'], ...decisions];
  }
  const initialSelectedFilters = {
    segment: '',
    brand: '',
    subbrand: '',
    size: '',
    sku: '',
    algoreco: '',
    assignedTo: '',
    region: null,
    customer: null
  };
  const [selectedFilters, setSelectedFilters] = useState(initialSelectedFilters);
  const multiVoteComponentLabels = {
    title: 'Voting',
    dropdown: 'Recommendation (Max 24 char)*',
    inputFrom: 'Request input from',
    comment: 'Comments*',
    buttonLabel: 'Apply Votes'
  };
  const { userDetails } = useOkta();
  const { roles, gpid, email, username } = userDetails;
  const getTotalVotes = (data) => {
    let count = 0;
    data.forEach((element) => {
      if (element.IsVotingDone) {
        count++;
      }
    });

    return count;
  };

  const checkProgress = (taskName, userType) => {
    let showProgress = false;
    if (taskName === 'Steering Reco') {
      if (userType === CORE_USER || userType === TPO_ADMIN) {
        showProgress = true;
      }
    } else if (taskName === 'Regional Decisions') {
      if (
        userType === REGIONAL_USER_TYPE ||
        userType === CHANNEL_USER_TYPE ||
        userType === TPO_ADMIN
      ) {
        showProgress = true;
      }
    } else {
      if (taskName === 'Functional Feedback') {
        if (
          userType !== REGIONAL_USER_TYPE &&
          userType !== CHANNEL_USER_TYPE &&
          userType !== CORE_USER
          // && userType !== TPO_ADMIN
        ) {
          showProgress = true;
        }
      }
    }

    return showProgress;
  };

  const checkDrawer = (taskName, userType) => {
    let showDrawer = false;
    if (taskName === 'Steering Reco') {
      if (userType === CORE_USER || userType === TPO_ADMIN) {
        showDrawer = true;
        setShowUploadDownload(false);
      }
    } else if (taskName === 'Regional Decisions') {
      // Added TPO_ADMIN here to allow Regional voting for TPO_ADMIN
      if (
        userType === REGIONAL_USER_TYPE ||
        userType === CHANNEL_USER_TYPE ||
        userType === TPO_ADMIN
       
      ) {
        showDrawer = true;
        setShowUploadDownload(false);

      } else if (userType === CORE_USER) {
      // CORE_USER should NOT see drawer but buttons should be enabled
      setShowUploadDownload(false);
      }
    } else if (taskName === 'Functional Feedback') {
      if (
        userType !== CORE_USER && 
        userType !== REGIONAL_USER_TYPE &&
        userType !== CHANNEL_USER_TYPE &&
        userType !== GENERAL_USER
        // Removed !== TPO_ADMIN here to allow Functional voting for TPO_ADMIN
        // && userType !== TPO_ADMIN
      ) {
        showDrawer = true;
        setShowUploadDownload(false);
      }  else if (userType === CORE_USER || userType == TPO_ADMIN) {
      // CORE_USER should NOT see drawer but buttons should be enabled
      setShowUploadDownload(false);
      }
    } else {
      if (userType === CORE_USER || userType == TPO_ADMIN) {
        showDrawer = true;
        setShowUploadDownload(false);
      }
    }

    return showDrawer;
  };

  
  // Fetching voting data in chunk
  const getVotingDataV2 = async (
    bu,
    gpid,
    userLevel,
    taskName,
    userFunctionName,
    region,
    pageSize,
    customer
  ) => {
    let allData = [];
    let shouldContinue = true;
    let pageNo = 0;
    let totalRecordsCount = 0;
    setIsTableLoading(true);
    try {
      while (shouldContinue) {
        const response = await fetchVotingDataV2(
          bu,
          gpid,
          userLevel,
          pageNo,
          pageSize,
          region,
          selectedCycleId,
          customer,
          taskName
        );
        const { totalRecordsCountForPagination, content, totalPagesCountForPagination, error } =
          response;
        if (error) {
          triggerNotification(error, 'error');
          return;
        }

        // If on the first page and no data is returned, stop further API calls
        if (pageNo === 0 && (!content || content.length === 0)) {
          shouldContinue = false;
          break;
        }

        allData?.push(...content);
        totalRecordsCount = totalRecordsCountForPagination;
        if (totalPagesCountForPagination === pageNo + 1 || Array.isArray(customer)) {
          shouldContinue = false;
        } else {
          pageNo++;
        }
      }

      setTotalSKUCount(totalRecordsCount); // progress bar denominator
      const totalVotes = getTotalVotes(allData);
      setTotalVoted(totalVotes); // progress bar numerator
      setFilteredLevel2Data(allData?.filter((v) => v.level === 2));

      await getUniqueL7Customers().then((r) => {
        if (r?.length) {
          setUniqueL7CustomersList(r);
        }
      });
      const customData = getData(allData, 'RecommendationTable',taskName);
      const drawervisible = customData?.length>0 && checkDrawer(taskName, userFunctionName);

      const filteredColumns = getColumns(
        restructureData(
          allData?.map((item) => {
            if (drawervisible) {
              delete item[
                taskName === 'Steering Reco' ? 'action reco' : 'steering team recommendation'
              ];
            }
            return item;
          })
        ),
        taskName,
        drawervisible,
        userLevel === 4 ? true : false,
        region
      );
      const hideLabels = ['LEVEL', 'LEVEL_ATTRIBUTE_ID', 'LEVEL_ATTRIBUTE_NAME', 'INVENTORY'];

      const updatedColumns = filteredColumns
        ?.filter((item) => !hideLabels.includes(item.label))
        .map((column) => {
          let category = ''; // Default category

          if (perforamceMetric.includes(column.id)) {
            category = 'Performance Metrics';
          } else if (operationalInp.includes(column.id)) {
            category = 'Importance Inputs';
          } else if (decisions.includes(column.id)) {
            category = 'Decisions';
          } else if (productAttr.includes(column.id)) {
            category = 'Product Attributes';
          }

          return {
            ...column,
            category
          };
        })
        .sort((a, b) => {
          const normalizedA = a.id.toLowerCase();
          const normalizedB = b.id.toLowerCase();

          const orderA = feedbackCollationColumnOrder[normalizedA] || 99999;
          const orderB = feedbackCollationColumnOrder[normalizedB] || 99999;

          return orderA - orderB;
        });

      setColumns(updatedColumns);
      dispatch(setVotingTableData(customData));
      setVotingData(customData);
      setFullResData(customData);
    } catch (error) {
      console.error('Error fetching voting data:', error);
    } finally {
      setIsTableLoading(false);
    }
  };

  const getRecommendationOptionData = useCallback(async (bu) => {
    try {
      const { content } = await fetchRecommendationOptions(bu);
      dispatch(setRecommendationOptions(content));
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  }, []);

  // Fetching Users in BU
  const getUsers = useCallback(async (bu, userLevel) => {
    try {
      const { content } = await fetchUsers(bu, userLevel);
      let filteredList = getSanitizedMemberList(content);
      dispatch(setTagUsers(filteredList));
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  });

  //Fetching region option
  const getRegionData = useCallback(
    async (taskName, userFunctionName, userid, userLevel, stepLevel) => {
      try {
        const { content } = await getRegionOptions(userid, userLevel, bu);

        setSelectedFilters((prev) => ({
          ...prev,
          ['region']: content?.[0]
        }));

        fetchRegionalVotingData(taskName, stepLevel, userFunctionName, content[0]);
        setRegionOption(content);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    }
  );

  const fetchRegionalVotingData = (taskName, steplevel, userFunctionName, region) => {
    const selectedRegion = region ? region : selectedFilters.region;
    //! added v2 chunk changes
    getVotingDataV2(
      bu,
      gpid,
      steplevel,
      taskName,
      userFunctionName,
      selectedRegion,
      perPageRowCount,
      selectedFilters?.customer
    );
  };

  const getUserLevelInfo = (bu) => {
    const cycleId = selectedCycleId || Number(localStorage?.getItem('cycle_id'));
    if (cycleId) {
      try {
        setIsLoading(true);
        getStepStatus(gpid, roles[0], bu, cycleId).then((response) => {
          const { userFunctionName, userLevel, stepperStatus, finalDecision } = response;
          const taskName =
            stepperStatus?.filter((item) => item.status === 'In Progress')[0]?.taskName || '';
          let steplevel = 0;
          stepperStatus?.map((item, index) => {
            if (stepperStatus.filter((item) => item.status === 'In Progress')?.length > 0) {
              if (item.status === 'In Progress') {
                steplevel = index + 1;
              }
            } else {
              const stepNotStartedLength = stepperStatus.filter(
                (item) => item.status === 'Not Started'
              )?.length;
              const stepCompletedLength = stepperStatus.filter(
                (item) => item.status === 'Completed'
              )?.length;
              if (stepNotStartedLength === 3) {
                steplevel = 1;
              } else if (stepNotStartedLength === 2) {
                steplevel = 2;
              } else if (stepNotStartedLength === 1) {
                steplevel = 3;
              } else if (stepCompletedLength === 3) {
                steplevel = 4;
              } else if (stepCompletedLength === 2) {
                steplevel = 3;
              } else if (stepCompletedLength === 1) {
                steplevel = 2;
              }
            }
          });
          const stepperCompletedLength = stepperStatus?.filter(
            (item) => item.status === 'Completed'
          )?.length;
          if (stepperCompletedLength === 3) {
            setShowVotingViewScreen(true);
            steplevel = 4;
          }
          setStepLevelState(stepperCompletedLength === 3 ? 4 : steplevel);
          dispatch(setStepperStatus(response));
          setDisableDecisionButton(finalDecision?.status === 'In Progress' ? false : true);
          setUserType(userFunctionName);
          setUserLevel(userLevel);
          //change this
          setCurrentVotingTeam(taskName);
          setTasks(stepperStatus);
          setIsLoading(false);
          getUsers(bu, userLevel);

          if (taskName === 'Regional Decisions') {
            getRegionData(taskName, userFunctionName, gpid, userLevel, steplevel);
          } else {
            fetchRegionalVotingData(taskName, steplevel, userFunctionName, selectedFilters.region);
          }
        });
      } catch (error) {
        setIsLoading(false);
      } finally {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    if (bu && gpid) {
      getUserLevelInfo(bu);
          getRecommendationOptionData(bu);
    }
    const isProgressVisible = checkProgress(currentVotingTeam, userType);
    setProgressVisible(isProgressVisible);
  }, [bu, gpid, currentVotingTeam, userType]);

  // Multi-votes component handle functions
  const getVoteData = (voteData, checkedRows) => {
    const tableData = votingTableData;
    const updatedRow = tableData.map((row) => {
      // If the row is checked, update the corresponding data with voteData
      if (checkedRows.includes(row.CycleSKUKey)) {
        return {
          ...row,
          agreeDisagreeStatus: voteData.thumbStatus,
          recommendation: voteData.recoName,
          comment: voteData.comment,
          taggedUsers: voteData.selectedOptions
        };
      }
      return row; // Otherwise, return the original row
    });
    dispatch(setVotingTableData(updatedRow));
    setAreVotesApplied((prev) => !prev);
    setDisableUndo(false);
    triggerNotification(`Votes submitted for ${checkedRows?.length} SKUs`, 'success');
  };

  // Reseting VoteData
  const resetVoteData = () => {
    dispatch(setVotingTableData(votingData));
    setDisableUndo(true);
    setCheckedRows([]);
  };

  const getUniqueL7Customers = async () => {
    return await fetchUniqueL7Customers(selectedCycleId, bu);
  };

  // Clicking save button for sending post API
  const saveVotes = async () => {
    const rows = checkedRows?.length > 0 ? checkedRows : editedRows;
    const tableData = votingTableData;
    const updated = tableData.filter((row) => rows.includes(row.CycleSKUKey));
    let showNotification = false;
    updated?.map((item) => {
      // If functional step , skip recommendation validation
      if (currentVotingTeam === 'Functional Feedback') {
        if (item.agreeDisagreeStatus === false && !item.comment?.trim()) {
          showNotification = true;
        }
      } else {
        if (
          item.agreeDisagreeStatus === false &&
          (!item.recommendation?.trim() || !item.comment?.trim())
        ) {
          showNotification = true;
        }
      }
    });
    const showSaveNotification = false;
    if (showSaveNotification) {
      triggerNotification('Apply votes or deselect SKUs to Save changes!', 'error');
    }

    const voteData = [];
    updated.map((item) => {
      const users = item?.taggedUsers
        ?.filter((item) => item.UserName !== null)
        ?.map((user) => {
          return {
            CycleSKUKey: item.CycleSKUKey,
            UserEmail: user.UserEmail,
            UserName: user.UserName,
            CreatedBy: email,
            ModifiedBy: ''
          };
        });
      voteData.push({
        CycleSKUKey: item.CycleSKUKey,
        Level: userLevel, // will come from level state variable
        VoteOwnerFunctionName: userType,
        AgreeDisagreeStatus: item.agreeDisagreeStatus,
        Recommendation: item.recommendation,
        Comment: item.comment,
        CreatedBy: email,
        ModifiedBy: '',
        TaggedUsers: users //item.taggedUsers
      });
    });
    //remove duplicacy if any
    const uniquevote = voteData?.filter((item, index, self) => {
      return (
        index ===
        self.findIndex(
          (t) =>
            t.CycleSKUKey === item.CycleSKUKey &&
            t.Level === item.Level &&
            t.VoteOwnerFunctionName === item.VoteOwnerFunctionName &&
            t.Recommendation === item.Recommendation &&
            t.Comment === item.Comment &&
            t.CreatedBy === item.CreatedBy
        )
      );
    });

    let payload = {
      userId: gpid,
      role: roles[0],
      level: userLevel,
      voteOwnerFunctionName: userType,
      votingVoteRegistryEntityList: [...new Set(uniquevote)]
    };

    if (currentVotingTeam === 'Regional Decisions') {
      payload = {
        ...payload, // Spread existing payload properties
        region: selectedFilters.region // Add the region property
      };
    }

    if (showNotification) {
      triggerNotification(
        'Please provide recommendation and comment for SKUs you will disagree with!',
        'error'
      );
    } else {
      setSaveLoader(true);
      const res = await saveVotingData(payload); //api call
      if (res) {
        triggerNotification(
          `Votes successfully submitted for ${checkedRows?.length} SKUs`,
          'success'
        );
      }
      dispatch(setEditedRows([]));
      setCheckedRows([]);
      setAreVotesApplied((prev) => !prev);
      setAreVotesSaved(true);
      if (res.length > 0) {
        setSaveLoader(false);
      }
      // disabling all buttons after save
      setShowApply(true);
      dispatch(setShowSave(true));
      setShowUndo(true);
      //! added chunk api call changes
      // if customer filters are applied, show customer data after submit votes else show national data
      if (selectedFilters?.level_attribute_name) {
        setCallApply(selectedFilters);
      } else {
        getVotingDataV2(
          bu,
          gpid,
          stepLevelState,
          currentVotingTeam,
          userType,
          selectedFilters?.region,
          perPageRowCount,
          selectedFilters?.customer
        );
      }
      // Resetting states after saving
    }
  };

  useEffect(() => {
    // First, call getFileUploadStatus immediately
    if (userDetails?.email !== '') {
      getFileUploadStatus();
    }
  }, [userDetails?.email]);

  useEffect(() => {
    if (Object.entries(uploadStatusMsg).length) {
      // Only set interval if it's not already set
      if (!intervalIdRef.current) {
        intervalIdRef.current = setInterval(() => {
          getFileUploadStatus();
        }, 30000); // 30 seconds interval
      }
    } else {
      // If `uploadStatusMsg` is empty, clear the interval
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
    }
    // Cleanup: Clear the interval when the component unmounts
    return () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
    };
  }, [uploadStatusMsg]);

  // Upload file
  const getFileUploadStatusByUserID = async (user, bu) => {
    try {
      let response = await fetchUploadsInProgressByUserId(user, bu);
      setUploadStatusMsg({});
      if (response) {
        if (response?.status === 'Success') {
          getVotingDataV2(
            bu,
            gpid,
            stepLevelState,
            currentVotingTeam,
            userType,
            selectedFilters?.region,
            perPageRowCount,
            selectedFilters?.level_attribute_name
          );
          triggerNotification(response?.message + ' ' + response?.description, 'success');
        } else if (response?.status === 'Failed') {
          triggerNotification(response?.message + ' ' + response?.description, 'error');
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const getFileUploadStatus = async () => {
    try {
      let response = await fetchUploadsInProgress(bu);

      setUploadStatusMsg(response);
      if (Object.entries(response).length == 0) {
        getFileUploadStatusByUserID(userDetails.email, bu);

        if (intervalIdRef.current) {
          clearInterval(intervalIdRef.current);
          intervalIdRef.current = null; // Clear the reference as well
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const setCallReset = () => {
    setSelectedFilters(initialSelectedFilters);
    setFilterPagination(!filterPagination);
    return getUserLevelInfo(bu);
  };

  const setCallApply = (params) => {
    if (params?.level_attribute_name) {
      return getVotingDataV2(
        bu,
        gpid,
        stepLevelState,
        currentVotingTeam,
        userType,
        selectedFilters?.region,
        perPageRowCount,
        params?.level_attribute_name
      );
    }
    setFilterPagination(!filterPagination);
  };

  const getFilteredCustomerList = (data, selectedFilters) => {
    let config = [];
    switch (currentVotingTeam) {
      case 'Steering Reco':
        config = steeringReco;
        break;
      case 'Functional Feedback':
        config = functionalFeedback;
        break;
      case 'Regional Decisions':
        config = regionalDecisions;
        break;
      default:
        config = finalDecisions;
    }
    let filters = { ...selectedFilters };
    if (selectedFilters.level_attribute_name?.length > 0) {
      delete filters.level_attribute_name;
    }
    const filteredData = data.filter((item) => {
      return config.every(({ key }) => {
        const selectedValuesForKey = filters[key] || [];
        const normalizedItemValue =
          key === 'votes aligned'
            ? item[key] === true
              ? 'Yes'
              : item[key] === false
                ? 'No'
                : ''
            : item[key];

        return (
          selectedValuesForKey.length === 0 ||
          (Array.isArray(normalizedItemValue)
            ? selectedValuesForKey.some((val) => normalizedItemValue.includes(val)) // Check for intersection
            : selectedValuesForKey.includes(normalizedItemValue)) // Direct match for non-array fields
        );
      });
    });
    return filteredData;
  };

  return (
    <>
      {isLoading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center', // Center horizontally
            alignItems: 'center', // Center vertically
            height: '100vh' // Take full viewport height
          }}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack gap="16px" sx={{ padding: '24px', width: '100%' }}>
          {/* Page header + Progress bar */}
          <Stack direction="column" width="100%">
            <PageHeader
              pageHeader={'Feedback & Voting'}
              showBu={false}
              showCycle={true}
              cardHeaderDetails={{
                cycleName: selectedCycle
              }}
            />
          </Stack>
          <Stack direction={'row'} justifyContent={'flex-end'}>
            {!showVotingViewScreen && progressVisible && (
              <Box
                sx={{ minWidth: '400px', backgroundColor: 'none', padding: '5px', margin: '5px' }}>
                <LinearProgressWithLabel value={totalVoted} totalValue={totalSKUCount} />
              </Box>
            )}
          </Stack>

          {showVotingViewScreen ? (
            <Paper
              elevation={3}
              sx={{
                p: 2,
                borderRadius: '8px',
                backgroundColor: '#FBFCFE',
                boxShadow: '4px 0px 24px -2px #005CBC14',
                textAlign: 'center',
                height: '140px'
              }}>
              <Typography
                variant="h5"
                style={{
                  margin: '10px 0px'
                }}>{`Hey ${username} ! The voting process is completed.`}</Typography>
              <Button
                variant="contained"
                color="primary"
                style={{ margin: '10px 0px' }}
                disabled={disableDecisionButton}
                onClick={() =>
                  navigate(
                    getUrlWithParams(
                      '/alignment-action/decision-making-central',
                      bu,
                      selectedCycle,
                      selectedCycleId
                    )
                  )
                }>
                Proceed with the Decisions{' '}
                <ArrowForwardIcon style={{ width: '18px', height: '18px' }} />
              </Button>
            </Paper>
          ) : (
            <>
              {/* heading + Stepper component */}
              <Stack alignItems={'center'}>
                <VotingStepper
                  tasks={tasks}
                  loading={isLoading}
                  userLevel={userLevel}
                  userType={userType}
                />
              </Stack>
              {/* Filters Screen */}
              <Stack sx={{ padding: '10px' }}>
                <VotingFilter
                  tableName={'voting table'}
                  isTableLoading={isTableLoading}
                  saveLoader={saveLoader}
                  checkedRows={checkedRows}
                  areVotesApplied={areVotesApplied}
                  saveVotes={saveVotes}
                  showApply={showApply}
                  showUndo={showUndo}
                  currentVotingTeam={currentVotingTeam}
                  votingData={votingData}
                  setSelectedFilters={(region) => {
                    // Temporary fix but need to remove string check and need to fix at global filters level logic
                    let filters =
                      typeof region === 'string'
                        ? { ...selectedFilters, region: region }
                        : {
                            ...selectedFilters,
                            ...region
                          };
                    setSelectedFilters(filters);
                  }}
                  selectedFilters={selectedFilters}
                  gpid={gpid}
                  userLevel={userLevel}
                  regionOption={regionOption}
                  fetchRegionalVotingData={fetchRegionalVotingData}
                  userType={userType}
                  stepLevelState={stepLevelState}
                  showSaveButton={showVotingViewScreen}
                  setCallReset={() => setCallReset()}
                  setCallApply={(filters) => setCallApply(filters)}
                  filteredData={selectedFilters?.customer ? filteredLevel2Data : fullResData}
                  customerFilter={uniqueL7CustomersList}
                  selectedCustomer={selectedFilters?.customer}
                  setFilterPagination={(filterPagination) => setFilterPagination(filterPagination)}
                  filterPagination={filterPagination}
                />
              </Stack>
              {/* MultiVotes Screen */}
              {currentVotingTeam && (
                <Stack alignItems={'center'}>
                  <MultiVotingCard
                    checkedRows={checkedRows}
                    setVote={(vote) => setVote(vote)}
                    multiVoteComponentLabels={multiVoteComponentLabels}
                    parentComponent={'Voting'}
                    getVoteData={getVoteData}
                    resetVoteData={resetVoteData}
                    disableUndo={disableUndo}
                    setAreVotesSaved={(areVotesSaved) => setAreVotesSaved(areVotesSaved)}
                    areVotesSaved={areVotesSaved}
                    recommendationOptions={recommendationOptions}
                    tagUsers={tagUsers}
                    currentVotingTeam={currentVotingTeam}
                    setShowApply={(showApply) => setShowApply(showApply)}
                    setShowUndo={(showUndo) => setShowUndo(showUndo)}
                    showApply={showApply}
                    showUndo={showUndo}
                  />
                </Stack>
              )}
            </>
          )}
          <SteeringRecoTable
            data={getFilteredCustomerList(votingTableData, selectedFilters)}
            columns={columns}
            isTableLoading={isTableLoading}
            checkedRows={checkedRows}
            editedFCRows={editedRows}
            setCheckedRows={setCheckedRows}
            vote={vote}
            currentVotingTeam={currentVotingTeam}
            showCheckbox={showVotingViewScreen}
            setUploadStatusMsg={(data) => setUploadStatusMsg(data)}
            userLevel={userLevel}
            currentActiveCycle={selectedCycle}
            currentActiveCycleID={selectedCycleId}
            userType={userType}
            regionOption={selectedFilters.region}
            isUploadDownloadDisabled={
              showUploadDownload || Object.entries(uploadStatusMsg).length !== 0
            }
            customerFilter={selectedFilters?.level_attribute_name}
            setFilterPagination={(filterPagination) => setFilterPagination(filterPagination)}
            filterPagination={filterPagination}
          />
          {!showVotingViewScreen && (
            <Box
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                backgroundColor: '#D7E5F8',
                padding: '15px 50px',
                borderRadius: '8px',
                boxShadow: '0px 4px 4px 0px #00000040',
                position: 'sticky',
                bottom: 1,
                zIndex: '100'
              }}>
              <Tooltip
                direction="top-left"
                maxWidth="100px"
                text="If done voting, update your calendar tasks here">
                <Button
                  id="save-filter"
                  variant="text"
                  color="primary"
                  size="medium"
                  sx={{
                    height: '32px',
                    width: '175px'
                  }}
                  onClick={() =>
                    navigate(
                      getUrlWithParams('/readiness/calendar', bu, selectedCycle, selectedCycleId)
                    )
                  }>
                  Go To Calendar
                </Button>
              </Tooltip>
              <Button
                id="save-filter"
                variant="contained"
                color="primary"
                size="medium"
                disabled={saveStatus ? true : false}
                sx={{
                  height: '32px',
                  width: '175px',
                  marginLeft: '5%'
                }}
                onClick={saveVotes}>
                Submit Votes
              </Button>
            </Box>
          )}
        </Stack>
      )}
    </>
  );
};

export default FeedbackCollation;
